import { isBefore } from 'date-fns';
import { TFunction } from 'i18next';
import React, { Dispatch, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 } from 'uuid';

import { DesignToolSeatModel } from '../../../../../../typings/api/skymap/rest/v0/.common';
import { CompanyAdmin } from '../../../../../../typings/api/skymap/rest/v0/company';
import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { formattedDate, formattedDateTime } from '../../../../utils/dateUtils';
import { isDefined } from '../../../../utils/variables';
import { useDialog } from '../../../hooks/use-dialog';
import { useErrorHandling } from '../../../hooks/use-error-handling';
import type { GetCompanyResponse } from '../../../state/company-state';
import { useCompany } from '../../../state/company-state';
import { Button } from '../../button/button';
import { DateTimeInput } from '../../date-time-input/date-time-input';
import { Dialog } from '../../dialog/dialog';
import { InfoBox } from '../../info-box/info-box';
import { LabelledContainer } from '../../labelled-container/labelled-container';
import { OverlayLoader } from '../../overlay-loader/overlay-loader';
import { Select } from '../../select/select';
import { Stack } from '../../stack/stack';
import { TextBox } from '../../text-box/text-box';
import { DispatchLicenseTableItem, LicenseTableItem } from './company-licenses';

type CompanyAdminItem = {
  id: string;
  name: string;
  admin?: CompanyAdmin;
};

type Props = {
  dialog: ReturnType<typeof useDialog>;
  item?: LicenseTableItem;
  dispatchAction: Dispatch<DispatchLicenseTableItem>;
};

function createItem(
  seat: DesignToolSeatModel,
  company: GetCompanyResponse,
  t: TFunction,
): LicenseTableItem {
  const createdAt = seat?.createdAt ? new Date(seat.createdAt) : new Date();
  const validUntil = seat?.validUntil ? new Date(seat.validUntil) : undefined;
  const admin = seat.userId
    ? company.companyAdmins.find((x) => x.userId === seat.userId)
    : undefined;

  return {
    id: seat.id!,
    assignedTo: admin?.email ?? seat.userId ?? t('licenses.values.unassigned', { ns: 'companies' }),
    status: !isDefined(validUntil) || isBefore(Date.now(), validUntil) ? 'ACTIVE' : 'EXPIRED',
    type: 'DESIGN_SEAT',
    createdBy: '',
    validUntil,
    validUntilText: isDefined(validUntil)
      ? formattedDateTime(validUntil)
      : t('licenses.values.noEndDate', { ns: 'companies' }),
    userId: admin?.userId,
    createdAt,
    createdAtText: formattedDateTime(createdAt),
  };
}

export const DesignSeatDialog = (props: Props) => {
  const { t } = useTranslation();
  const company = useCompany();
  const [isLoading, setIsLoading] = useState(false);
  const { buildErrorList, handleError, clearErrors } = useErrorHandling({
    displayNonApiErrors: true,
  });
  const hasCompanyAdminBasedDesignLicenses = useRef<boolean>(
    company.licenses.some((x) => x.id === 'company_admin' || x.type === 'DESIGN_SEAT_DEMO'),
  );

  const showEditButtons = !hasCompanyAdminBasedDesignLicenses.current;

  const admins = useRef<CompanyAdminItem[]>(
    company.companyAdmins.map((x) => {
      return {
        id: v4(),
        name: `${x.email} (${x.name})`,
        admin: x,
      };
    }),
  );
  const noneItem = useRef<CompanyAdminItem>({
    id: v4(),
    name: t('licenses.values.unassigned', { ns: 'companies' }),
    admin: undefined,
  });

  const [validUntil, setValidUntil] = useState(props.item?.validUntil);

  const [disableComboBox, setDisableComboBox] = useState(
    isDefined(props.item?.userId) &&
      !admins.current.some((x) => x.admin?.userId === props.item?.userId),
  );
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(props.item?.userId);
  const [selectedItem, setSelectedItem] = useState<CompanyAdminItem>(
    (isDefined(props.item?.userId)
      ? admins.current.find((x) => x.admin?.userId === props.item?.userId)
      : undefined) ?? noneItem.current,
  );

  useEffect(() => {
    if (!disableComboBox) {
      setSelectedUserId(selectedItem.admin?.userId);
      const matchByUserId = items.current.find(
        (x) => x.admin?.userId === selectedItem.admin?.userId,
      );
      if (matchByUserId) {
        setSelectedItem(matchByUserId);
      }
    }
  }, [selectedItem, disableComboBox]);

  const items = useRef([noneItem.current, ...admins.current]);

  const wrapWithErrorHandling = async (callback: () => Promise<void>) => {
    try {
      clearErrors();
      setIsLoading(true);
      await callback();
    } catch (err) {
      handleError(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isDefined(props.item) && props.item.type !== 'DESIGN_SEAT') {
    return null;
  }

  return (
    <Dialog
      closeIcon={true}
      closeOnDimmerClick={true}
      useGoogleTranslate={false}
      width={500}
      onClose={props.dialog.hide}
    >
      {{
        header: isDefined(props.item) ? 'Redigera designsäte' : 'Skapa designsäte',
        content: (
          <OverlayLoader visible={isLoading}>
            {hasCompanyAdminBasedDesignLicenses.current ? (
              <InfoBox color="yellow">
                Det går inte att skapa designsäten på företaget eftersom det finns en
                DESIGN-licenser som är direkt knutna till företagsadministratörer.
              </InfoBox>
            ) : (
              <Stack spacing={1}>
                <LabelledContainer
                  text={`${t('licenses.assignedTo', { ns: 'companies' })} (admin)`}
                >
                  <Select
                    disabled={disableComboBox}
                    options={
                      disableComboBox
                        ? [
                            {
                              id: undefined,
                              name: `${t('licenses.assignedTo', { ns: 'companies' })} ID`,
                            },
                          ]
                        : items.current
                    }
                    value={disableComboBox ? undefined : selectedItem.id}
                    onChange={(e) => {
                      const matchByItemId = items.current.find((x) => x.id === e.target.value);
                      if (isDefined(matchByItemId)) {
                        setSelectedItem(matchByItemId);
                      }
                    }}
                  />
                </LabelledContainer>

                <LabelledContainer text={`${t('licenses.assignedTo', { ns: 'companies' })} (ID)`}>
                  <TextBox
                    placeholder={t('licenses.values.unassigned', { ns: 'companies' })}
                    value={selectedUserId ?? ''}
                    onChange={(e) => {
                      const value = e.target.value.trim().length > 0 ? e.target.value : undefined;

                      setSelectedUserId(value);
                      const matchByUserId = items.current.find((x) => x.admin?.userId === value);
                      setDisableComboBox(!isDefined(matchByUserId));

                      // If the input value matches an existing item, then we need to sync the combobox too.
                      if (isDefined(matchByUserId)) {
                        setSelectedItem(matchByUserId);
                      }
                    }}
                  />
                </LabelledContainer>

                <LabelledContainer text={t('licenses.validUntil', { ns: 'companies' })}>
                  <DateTimeInput
                    dateFormatter={(date) => formattedDate(date)}
                    displayTime={false}
                    initialValue={validUntil}
                    placeholder={t('licenses.values.noEndDate', { ns: 'companies' })}
                    onCleared={() => setValidUntil(undefined)}
                    onDateChoosen={(date) => setValidUntil(date)}
                  />
                </LabelledContainer>

                {buildErrorList()}
              </Stack>
            )}
          </OverlayLoader>
        ),
        footer: {
          left: (
            <>
              {isDefined(props.item) && (
                <Button
                  color="error"
                  disabled={isLoading}
                  leftIcon={{ icon: ['fad', 'trash-alt'] }}
                  variant="contained"
                  onClick={() =>
                    wrapWithErrorHandling(async () => {
                      await SkyMapAxiosServiceFactory.instance
                        .createCompanyServiceV0()
                        .deleteDesignToolSeat({
                          path: {
                            companyId: company.id,
                            designToolSeatId: props.item!.id,
                          },
                        });
                      props.dispatchAction({
                        type: 'REMOVE',
                        id: props.item!.id,
                      });
                      props.dialog.hide();
                    })
                  }
                >
                  {t('delete', { ns: 'common' })}
                </Button>
              )}
            </>
          ),
          right: (
            <>
              {isDefined(props.item) && showEditButtons && (
                <Button
                  color="primary"
                  disabled={isLoading}
                  variant="contained"
                  onClick={() =>
                    wrapWithErrorHandling(async () => {
                      const result = await SkyMapAxiosServiceFactory.instance
                        .createCompanyServiceV0()
                        .patchDesignToolSeat({
                          path: {
                            companyId: company.id,
                            designToolSeatId: props.item!.id,
                          },
                          body: {
                            userId: selectedUserId,
                            validUntil: validUntil?.toISOString(),
                          },
                        });
                      const items: LicenseTableItem[] = [];
                      for (const item of result.data) {
                        items.push(createItem(item, company, t));
                      }
                      props.dispatchAction({ type: 'UPDATE', items });
                      props.dialog.hide();
                    })
                  }
                >
                  {t('update', { ns: 'common' })}
                </Button>
              )}

              {!isDefined(props.item) && showEditButtons && (
                <Button
                  color="primary"
                  disabled={isLoading}
                  variant="contained"
                  onClick={() =>
                    wrapWithErrorHandling(async () => {
                      const result = await SkyMapAxiosServiceFactory.instance
                        .createCompanyServiceV0()
                        .createDesignToolSeat({
                          path: {
                            companyId: company.id,
                          },
                          body: {
                            companyId: company.id,
                            userId: selectedUserId,
                            validUntil: validUntil?.toISOString(),
                          },
                        });
                      const items: LicenseTableItem[] = [];
                      for (const item of result.data) {
                        if (isDefined(item.id)) {
                          items.push(createItem(item, company, t));
                        }
                      }
                      props.dispatchAction({ type: 'ADD', items });
                      props.dialog.hide();
                    })
                  }
                >
                  {t('create', { ns: 'common' })}
                </Button>
              )}

              <Button variant="text" onClick={props.dialog.hide}>
                {t('close', { ns: 'common' })}
              </Button>
            </>
          ),
        },
      }}
    </Dialog>
  );
};
